import React, { useState, useEffect } from 'react';
import axios from 'axios';

const RoomAndFloorStatus = () => {
  const [roomStatus, setRoomStatus] = useState({
    occupied: { total: 0, statuses: {} },
    available: { total: 0, statuses: {} },
  });
  const [floorStatus, setFloorStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRoomData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get('https://admin.thepridehotel.in/api/rooms'); // Adjust to your API
        const roomsData = response.data;

        const newRoomStatus = {
          occupied: { total: 0, statuses: {} },
          available: { total: 0, statuses: {} },
        };
        const newFloorStatus = {};

        roomsData.forEach(room => {
          if (room.status === 'Occupied') {
            newRoomStatus.occupied.total++;
            newRoomStatus.occupied.statuses[room.room_status] =
              (newRoomStatus.occupied.statuses[room.room_status] || 0) + 1;
          } else if (room.status === 'Available') {
            newRoomStatus.available.total++;
            newRoomStatus.available.statuses[room.room_status] =
              (newRoomStatus.available.statuses[room.room_status] || 0) + 1;
          }

          const floor = room.room_floor;
          if (!newFloorStatus[floor]) {
            newFloorStatus[floor] = { totalRooms: 0, availableRooms: 0 };
          }
          newFloorStatus[floor].totalRooms++;
          if (room.status === 'Available') {
            newFloorStatus[floor].availableRooms++;
          }
        });

        setRoomStatus(newRoomStatus);
        setFloorStatus(newFloorStatus);
        setLoading(false);
      } catch (err) {
        setError('Failed to load room data.');
        setLoading(false);
      }
    };

    fetchRoomData();
  }, []);

  if (loading) {
    return <div style={styles.loading}>Loading room and floor data...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  const CircularProgressBar = ({ percentage, label, totalRooms, availableRooms }) => (
    <div style={styles.floorProgressCard}>
      <h4 style={styles.floorHeading}>{label}</h4>
      <svg viewBox="0 0 36 36" width="80" height="80" style={styles.progressSvg}>
        <path
          d="M18 2.0845
             a 15.9155 15.9155 0 0 1 0 31.831
             a 15.9155 15.9155 0 0 1 0 -31.831"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="3"
        />
        <path
          d="M18 2.0845
             a 15.9155 15.9155 0 0 1 0 31.831
             a 15.9155 15.9155 0 0 1 0 -31.831"
          fill="none"
          stroke="#A76A3C"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${percentage}, 100`}
        />
        <text x="18" y="20.35" textAnchor="middle" fill="#111827" fontSize="7" transform="rotate(90 18 18)">
          {percentage.toFixed(0)}%
        </text>
      </svg>
      <p style={styles.floorRoomCount}>Total Rooms: <span style={styles.value}>{totalRooms}</span></p>
      <p style={styles.floorRoomCount}>Available: <span style={styles.value}>{availableRooms}</span></p>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h3 style={styles.heading}>Room status</h3>
        <div style={styles.roomGrid}>
          <div>
            <p style={styles.roomLabel}>
              <span style={styles.boldText}>Occupied rooms</span>{' '}
              <span style={styles.value}>{roomStatus.occupied.total}</span>
            </p>
            {Object.entries(roomStatus.occupied.statuses).map(([statusName, count]) => (
              <p key={statusName} style={styles.statusText}>
                {statusName.charAt(0).toUpperCase() + statusName.slice(1)}{' '}
                <span style={styles.value}>{count}</span>
              </p>
            ))}
          </div>
          <div>
            <p style={styles.roomLabel}>
              <span style={styles.boldText}>Available rooms</span>{' '}
              <span style={styles.value}>{roomStatus.available.total}</span>
            </p>
            {Object.entries(roomStatus.available.statuses).map(([statusName, count]) => (
              <p key={statusName} style={styles.statusText}>
                {statusName.charAt(0).toUpperCase() + statusName.slice(1)}{' '}
                <span style={styles.value}>{count}</span>
              </p>
            ))}
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={styles.heading}>Floor status</h3>
        <div style={styles.floorContainer}>
          {Object.entries(floorStatus).map(([floor, data]) => (
            <CircularProgressBar
              key={floor}
              label={floor}
              percentage={data.totalRooms > 0 ? (data.availableRooms / data.totalRooms) * 100 : 0}
              totalRooms={data.totalRooms}
              availableRooms={data.availableRooms}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    backgroundColor: '#F3F4F6',
    padding: '8px',
    margin: '0',
    fontFamily: 'Inter, sans-serif',
    justifyContent: 'center',
  },
  card: {
    backgroundColor: '#fff',
    padding: '10px',
    borderRadius: '14px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    flex: 1,
    minWidth: '300px',
    maxWidth: 'calc(50% - 10px)',
    boxSizing: 'border-box',
  },
  heading: {
    fontSize: '22px',
    fontWeight: 700,
    marginBottom: '16px',
    color: '#111827',
    textAlign: 'center',
  },
  roomGrid: {
    display: 'flex',
    justifyContent: 'space-around',
    gap: '15px',
  },
  roomLabel: {
    marginBottom: 'px',
    fontSize: '16px',
    color: '#6B7280',
    textAlign: 'center',
  },
  statusText: {
    fontSize: '15px',
    margin: '6px 0',
    color: '#111827',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 10px',
  },
  boldText: {
    fontWeight: 700,
    color: '#374151',
  },
  value: {
    marginLeft: 8,
    color: '#4B5563',
    fontWeight: 600,
  },
  floorContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    gap: '15px',
    marginTop: '10px',
  },
  floorProgressCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px',
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    backgroundColor: '#F9FAFB',
    minWidth: '120px',
    flex: 1,
    boxSizing: 'border-box',
  },
  floorHeading: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '8px',
    color: '#374151',
    textAlign: 'center',
  },
  progressSvg: {
    transform: 'rotate(-90deg)',
    marginBottom: '8px',
  },
  floorRoomCount: {
    fontSize: '14px',
    color: '#6B7280',
    margin: '2px 0',
    fontWeight: 500,
  },
  loading: {
    textAlign: 'center',
    fontSize: '1.2rem',
    color: '#4B5563',
    padding: '50px',
  },
  error: {
    textAlign: 'center',
    fontSize: '1.2rem',
    color: '#EF4444',
    padding: '50px',
  },
};

export default RoomAndFloorStatus;
